import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { getRequestBlob } from '@/api/api';
import {
  authorizedFileQueryKey,
  fileLinkApi,
  isAuthorizedFileLink,
  isFileUnavailable,
} from '@/common/file-links';

type Options = {
  /**
   * Sent as `X-Service-Id`. The vessel service gates a file on a role within the vessel it
   * belongs to, so a vessel image needs that vessel's id; a person's avatar is gated on the
   * bearer token alone and needs nothing here.
   */
  serviceId?: string;
};

/**
 * Turns a backend file link into something renderable.
 *
 * A private link is fetched through axios — which attaches the headers a browser will not —
 * and handed back as a `blob:` URL; anything else is passed through untouched, so a caller
 * never has to know which of the two it holds. The blob is cached under the link itself, so
 * the same face shown in three seat rows is downloaded once.
 */
export const useAuthorizedFileUrl = (
  link: null | string | undefined,
  { serviceId }: Options = {},
) => {
  const needsAuth = isAuthorizedFileLink(link);

  const { data: blob, isError } = useQuery({
    queryKey: authorizedFileQueryKey(link),
    enabled: needsAuth,
    // Nothing in this app replaces a file, so what arrived is what the link holds.
    staleTime: Infinity,
    retry: (failureCount, error) => !isFileUnavailable(error) && failureCount < 3,
    queryFn: () => getRequestBlob(fileLinkApi(link!), link!, serviceId),
  });

  const url = useMemo(() => {
    if (!needsAuth) return link ?? null;
    return blob ? URL.createObjectURL(blob) : null;
  }, [needsAuth, link, blob]);

  // Revoke only the object URLs created here; leave passed-through links alone.
  useEffect(() => {
    if (!url || !needsAuth) return;
    return () => URL.revokeObjectURL(url);
  }, [url, needsAuth]);

  return { url, error: isError, isLoading: needsAuth && !blob && !isError };
};
